import { makeStyles } from 'tss-react/mui';

/**
 * Visual parity with sampark-new's mobile sidebar sheet. Token values are the
 * same hardcoded copies documented in SideNav.styles.ts (legacy has no Tailwind
 * or CSS-variable theme layer — if sampark-new retunes its tokens, update here
 * by hand):
 *
 *   --primary            3 100% 66%      active item text + tint
 *   --sidebar-foreground 240 5.3% 26.1%  item text (70% opacity when inactive)
 *   --sidebar-accent     240 4.8% 95.9%  hover background
 *   --sidebar-border     220 13% 91%     dividers
 */
const PRIMARY = 'hsl(3, 100%, 66%)';
const PRIMARY_TINT = 'hsla(3, 100%, 66%, 0.1)';
const FOREGROUND = 'hsla(240, 5.3%, 26.1%, 0.7)';
const ACCENT = 'hsl(240, 4.8%, 95.9%)';
const BORDER = 'hsl(220, 13%, 91%)';

export const useNavPanelStyles = makeStyles()(() => ({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#fff'
  },
  // Header: p-4, hamburger + wordmark left, close right (per mobile mock)
  topRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 16
  },
  iconButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 6,
    border: 'none',
    background: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: ACCENT
    }
  },
  topIcon: {
    height: 24,
    width: 24
  },
  wordmark: {
    height: 24,
    flexGrow: 0
  },
  closeButton: {
    marginLeft: 'auto'
  },
  // Nav list: px-4 py-2, gap-2 between items
  navList: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '8px 16px'
  },
  link: {
    textDecoration: 'none',
    fontSize: 16,
    fontWeight: 400,
    color: FOREGROUND,
    display: 'flex',
    alignItems: 'center',
    // h-[44px] px-3 gap-4 in the new app
    height: 44,
    flexShrink: 0,
    padding: '0 12px',
    gap: 16,
    borderRadius: 8,
    transition: 'background-color 0.15s, color 0.15s',
    '&:hover': {
      backgroundColor: ACCENT
    }
  },
  activeLink: {
    color: PRIMARY,
    background: PRIMARY_TINT,
    fontWeight: 500,
    '&:hover': {
      backgroundColor: PRIMARY_TINT
    }
  },
  icon: {
    // size-5 in the new app
    width: 20,
    height: 20,
    display: 'flex',
    color: 'inherit',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  bottomSection: {
    borderTop: `1px solid ${BORDER}`,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  // Footer user row — mirrors sampark-new's UserMenu (h-12, avatar + name + chevrons)
  userRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    height: 48,
    padding: '0 8px',
    width: '100%',
    border: 'none',
    background: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: ACCENT
    }
  },
  avatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    width: 32,
    flexShrink: 0,
    borderRadius: 8,
    // bg-blue-100 / text-blue-700, as in sampark-new's AvatarFallback
    backgroundColor: 'hsl(214, 95%, 93%)',
    color: 'hsl(224, 76%, 48%)',
    fontSize: 13,
    fontWeight: 500,
    textTransform: 'uppercase'
  },
  userName: {
    flex: 1,
    fontSize: 16,
    fontWeight: 500,
    color: 'hsl(240, 5.3%, 26.1%)',
    textTransform: 'capitalize',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },
  chevrons: {
    height: 16,
    width: 16,
    flexShrink: 0,
    color: 'hsla(240, 5.3%, 26.1%, 0.5)'
  },
  userMenuCard: {
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    padding: 4,
    marginBottom: 8,
    backgroundColor: '#fff'
  },
  userMenuItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 8px',
    borderRadius: 6,
    fontSize: 15,
    color: FOREGROUND,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: ACCENT
    }
  },
  signOutItem: {
    // text-red-600 / hover red-50, as in sampark-new's sign-out row
    color: 'hsl(0, 72%, 51%)',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: 'hsl(0, 86%, 97%)'
    }
  }
}));
